import React, { useEffect, useState } from 'react';

import { Select } from 'components/select';

import styles from './tabs.module.scss';

export interface ITabItem {
	id: string;
	title: string;
	active: boolean;
	type:
		| 'ComponentInternalHighlightNews'
		| 'ComponentInternalHighlightEvents'
		| 'ComponentInternalHighlightSubsidies'
		| 'ComponentInternalHighlightPermits'
		| 'ComponentInternalHighlightMapsAndStats'
		| 'ComponentInternalHighlightTopics';
}

export interface ITabsProps {
	items: any[];
	children: (activeTab: ITabItem, tabs: ITabItem[]) => React.ReactNode;
}

export const Tabs = ({ items, children }: ITabsProps): React.JSX.Element => {
	const initialTabs: ITabItem[] = items.map((item, index) => {
		return {
			id: encodeURIComponent(item.title)
				.toLowerCase()
				.replaceAll(/\.|%[\da-z]{2}/gi, ''),
			title: item.title,
			active: index === 0,
			type: item.__typename
		};
	});

	const [tabs, setTabs] = useState<ITabItem[]>(initialTabs);

	const onClick = (tab: ITabItem): void => {
		const newTabs = tabs.map((item) => {
			if (item.type === tab.type) {
				return { ...item, active: true };
			}

			return { ...item, active: false };
		});

		setTabs(newTabs);
	};

	// Navigate tabs with keyboard
	const onKeyUp = (event: React.KeyboardEvent<HTMLElement>, tab: ITabItem): void => {
		const currentTabIndex = tabs.findIndex((item) => item.id === tab.id);
		const nextTab = tabs[currentTabIndex + 1];
		const prevTab = tabs[currentTabIndex - 1];

		if (event.key === 'ArrowRight' && nextTab) {
			const nextDivElement = (event.target as HTMLDivElement).nextElementSibling as HTMLDivElement;
			nextDivElement.tabIndex = 0;
			nextDivElement.focus();
			onClick(nextTab);
		}

		if (event.key === 'ArrowLeft' && prevTab) {
			const previousDivElement = (event.target as HTMLDivElement).previousElementSibling as HTMLDivElement;
			previousDivElement.tabIndex = 0;
			previousDivElement.focus();
			onClick(prevTab);
		}
	};

	const onChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
		const newTabs = tabs.map((item) => {
			if (item.type === event.target.value) {
				return { ...item, active: true };
			}

			return { ...item, active: false };
		});

		setTabs(newTabs);
	};

	useEffect(() => {
		setTabs(
			items.map((item, index) => ({
				id: encodeURIComponent(item.title)
					.toLowerCase()
					.replaceAll(/\.|%[\da-z]{2}/gi, ''),
				title: item.title,
				active: index === 0,
				type: item.__typename
			}))
		);
	}, [items]);

	return (
		<>
			<div className={styles.dropdown}>
				<Select
					options={tabs.map((tab) => ({ id: tab.type, label: tab.title }))}
					value={tabs.find((tab) => tab.active)?.type}
					onChange={onChange}
					fullWidth
				/>
			</div>

			<div className={styles.tabs}>
				{tabs.map((tab) => (
					<div
						key={tab.type}
						className={tab.active ? styles.active : undefined}
						role="button"
						onClick={() => onClick(tab)}
						onKeyUp={(event) => onKeyUp(event, tab)}
						tabIndex={tab.active ? 0 : -1}
					>
						{tab.title}
					</div>
				))}
			</div>

			<div className={styles.content}>{children(tabs.find((tab) => tab.active) || tabs[0], tabs)}</div>
		</>
	);
};

export default Tabs;
